/**
 * LemonSqueezyCheckout.jsx
 * Handles LemonSqueezy payment integration.
 * Works from Pakistan - Merchant of Record handles all taxes/compliance.
 *
 * "Simplicity is the ultimate sophistication." - Leonardo da Vinci
 */
import { useEffect, useState, useCallback } from "react";
import { api } from "../api";

/* ─── Load LemonSqueezy Script ─────────────────────────────────────────────── */
function loadLemonSqueezyScript() {
  return new Promise((resolve, reject) => {
    if (window.LemonSqueezy) return resolve(window.LemonSqueezy);
    if (document.querySelector('script[src*="lemon.js"]')) {
      // Script loading, wait for it
      const check = setInterval(() => {
        if (window.LemonSqueezy) {
          clearInterval(check);
          resolve(window.LemonSqueezy);
        }
      }, 100);
      setTimeout(() => { clearInterval(check); reject(new Error("Timeout")); }, 10000);
      return;
    }
    const s = document.createElement("script");
    s.src = "https://app.lemonsqueezy.com/js/lemon.js";
    s.defer = true;
    s.onload = () => {
      // LemonSqueezy auto-initializes, give it a moment
      setTimeout(() => resolve(window.LemonSqueezy || window.createLemonSqueezy), 500);
    };
    s.onerror = reject;
    document.head.appendChild(s);
  });
}

/* ─── Hook ───────────────────────────────────────────────────────────────── */
export function useLemonSqueezy() {
  const [ready, setReady] = useState(false);
  const [error, setError] = useState(null);
  const [config, setConfig] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Get config from backend
        const { data: cfg } = await api.get("/subscription/lemonsqueezy-config");
        if (cancelled) return;
        setConfig(cfg);

        if (!cfg.store_id) {
          setError("Payment system not configured yet.");
          return;
        }

        // Load LemonSqueezy script
        await loadLemonSqueezyScript();
        if (cancelled) return;

        setReady(true);
      } catch (err) {
        if (!cancelled) {
          console.error("LemonSqueezy init error:", err);
          setError("Payment system temporarily unavailable.");
        }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const openCheckout = useCallback((variantId, userEmail, userId) => {
    if (!config?.store_id || !variantId) {
      console.error("Missing store_id or variantId");
      return;
    }

    // Build checkout URL
    const checkoutUrl = `https://${config.store_slug || 'devxhouse'}.lemonsqueezy.com/checkout/buy/${variantId}?checkout[email]=${encodeURIComponent(userEmail || '')}&checkout[custom][user_id]=${userId}`;

    // Try overlay first, fallback to redirect
    if (window.LemonSqueezy?.Url) {
      window.LemonSqueezy.Url.Open(checkoutUrl);
    } else if (window.createLemonSqueezy) {
      window.createLemonSqueezy();
      setTimeout(() => {
        if (window.LemonSqueezy?.Url) {
          window.LemonSqueezy.Url.Open(checkoutUrl);
        } else {
          window.open(checkoutUrl, '_blank');
        }
      }, 300);
    } else {
      // Direct redirect as fallback
      window.open(checkoutUrl, '_blank');
    }
  }, [config]);

  return { ready, error, config, openCheckout };
}

/* ─── Component ──────────────────────────────────────────────────────────── */
export default function LemonSqueezyCheckout({ plan, userEmail, onSuccess }) {
  const { ready, error, config, openCheckout } = useLemonSqueezy();
  const [loading, setLoading] = useState(false);
  const [currentSub, setCurrentSub] = useState(null);

  // Load current subscription
  useEffect(() => {
    api.get("/subscription")
      .then(({ data }) => setCurrentSub(data))
      .catch(() => {});
  }, []);

  // Listen for successful checkout (LemonSqueezy sends postMessage)
  useEffect(() => {
    const handleMessage = async (event) => {
      if (event.origin.includes('lemonsqueezy.com')) {
        if (event.data?.event === 'Checkout.Success') {
          setLoading(false);
          try {
            const { data } = await api.get("/subscription");
            setCurrentSub(data);
            if (onSuccess) onSuccess(data);
          } catch (_) {}
        }
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [onSuccess]);

  // Map plan tier to variant ID
  const variantForPlan = {
    professional: config?.variant_professional,
    business: config?.variant_business,
    enterprise: config?.variant_enterprise,
  };

  const handleClick = () => {
    const token = localStorage.getItem("token");
    if (!token) {
      alert("Please log in to purchase a plan.");
      return;
    }

    const variantId = variantForPlan[plan.tier];
    if (!variantId) {
      alert("Payment is being configured. Please contact kronos@devxhouse.com or try again later.");
      return;
    }

    setLoading(true);

    // Get user ID from token (JWT payload)
    let userId = "unknown";
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      userId = payload.sub || payload.identity || "unknown";
    } catch (_) {}

    openCheckout(variantId, userEmail || "", userId);

    // Reset loading after timeout (user might close checkout)
    setTimeout(() => setLoading(false), 60000);
  };

  const isCurrentPlan = currentSub?.tier === plan.tier;
  const isFree = plan.tier === "free";

  // Free plan - just activate
  if (isFree) {
    return (
      <button
        className="w-full py-3 rounded-xl font-semibold text-sm transition-all bg-white/10 hover:bg-white/15 text-gray-300 border border-white/10"
        onClick={() => onSuccess && onSuccess({ tier: "free" })}
      >
        {plan.cta || "Get Started Free"}
      </button>
    );
  }

  // Error state - show contact sales
  if (error) {
    return (
      <button
        className="w-full py-3 rounded-xl font-semibold text-sm transition-all bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white"
        onClick={() => {
          window.open(`mailto:kronos@devxhouse.com?subject=Kronos ${plan.tier} Plan&body=Hi, I'd like to subscribe to the ${plan.tier} plan. Please assist.`, '_blank');
        }}
      >
        Contact Sales
      </button>
    );
  }

  // Current plan
  if (isCurrentPlan) {
    return (
      <button
        className="w-full py-3 rounded-xl font-semibold text-sm bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 cursor-default"
        disabled
      >
        Current Plan
      </button>
    );
  }

  // Loading state or not ready
  if (!ready && !error) {
    return (
      <button
        className="w-full py-3 rounded-xl font-semibold text-sm bg-white/5 text-gray-500 border border-white/10 cursor-wait"
        disabled
      >
        Loading...
      </button>
    );
  }

  // Main checkout button
  return (
    <button
      className={`w-full py-3 rounded-xl font-semibold text-sm transition-all ${
        loading
          ? "bg-white/10 text-gray-400 cursor-wait"
          : "bg-gradient-to-r from-cyan-500 to-purple-600 hover:from-cyan-400 hover:to-purple-500 text-white shadow-lg hover:shadow-cyan-500/25 hover:scale-[1.02]"
      }`}
      onClick={handleClick}
      disabled={loading}
    >
      {loading ? (
        <span className="flex items-center justify-center gap-2">
          <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeDasharray="50 20" />
          </svg>
          Processing...
        </span>
      ) : (
        plan.cta || "Subscribe"
      )}
    </button>
  );
}
